/**
 * Normalized RBAC from the Prism modularization inventory.
 *
 * Three layers, matching the spreadsheet:
 *
 * 1. **Persona** — Student, Faculty, or Admin (who the user is in a program).
 * 2. **Admin tier** — Tenet admin (ULA / Super Admin) or program admin (PLA).
 * 3. **Module admin roles** — scoped capabilities within licensed products
 *    (Admin - Clin Ed, Admin - Curriculum, and so on).
 *
 * Tenet admin holds every module role across every program. Program admin holds
 * an explicit subset for the programs in `administeredProgramIds`. Faculty and
 * student personas carry no module admin roles.
 */

import type { Product } from "@exxatdesignux/product-framework"

import { getLoginSession, type WorkspaceRole } from "@/lib/login-session"
import { isProductEntitled } from "@/lib/mock/product-catalog"
import {
  adminProgramIds,
  hasPersonaInProgram,
  moduleAdminRolesInProgram,
  normalizeProgramPersonas,
} from "@/lib/program-personas"

/** Module-scoped admin roles from "Proposed Roles for RBAC". */
export type ModuleAdminRole =
  | "clin-ed"
  | "exxat-one"
  | "site-placement"
  | "site-contract-requests"
  | "site-contract"
  | "curriculum"
  | "student-compliance"
  | "faculty-compliance"
  | "exam"
  | "course-eval"
  | "user-management"

export const MODULE_ADMIN_ROLES: readonly {
  value: ModuleAdminRole
  label: string
  description: string
}[] = [
  {
    value: "clin-ed",
    label: "Admin - Clin Ed",
    description: "Clinical Education module administration",
  },
  {
    value: "exxat-one",
    label: "Admin - ExxatOne",
    description: "Exxat One module administration",
  },
  {
    value: "site-placement",
    label: "Admin - Site and Placement",
    description: "Site and placement administration within Clin Ed",
  },
  {
    value: "site-contract-requests",
    label: "Admin - Site and Contract + My Requests",
    description: "Site, contract, and request administration",
  },
  {
    value: "site-contract",
    label: "Admin - Site and Contract",
    description: "Site and contract administration",
  },
  {
    value: "curriculum",
    label: "Admin - Curriculum",
    description: "Curriculum Mapping administration",
  },
  {
    value: "student-compliance",
    label: "Admin - Student Compliance",
    description: "Student compliance administration",
  },
  {
    value: "faculty-compliance",
    label: "Admin - Faculty Compliance",
    description: "Faculty compliance administration",
  },
  { value: "exam", label: "Admin - Exam", description: "Exam Management administration" },
  {
    value: "course-eval",
    label: "Admin - Course Eval",
    description: "Course evaluation administration",
  },
  {
    value: "user-management",
    label: "Admin - User management",
    description: "User management within assigned programs",
  },
]

/** Persona labels from the high-level inventory sheet. */
export type WorkspacePersona = "student" | "faculty" | "admin"

const PERSONA_FROM_ROLE: Record<WorkspaceRole, WorkspacePersona> = {
  student: "student",
  member: "faculty",
  administrator: "admin",
}

/**
 * Sellable modules each non-admin persona may open from home.
 *
 * Student: Clin Ed plus Compliance for their own clearance (routes to the student
 * compliance portal). Faculty: Faculty Touchpoints (Clin Ed + Compliance, plus
 * Curriculum Mapping and Exxat One when licensed). Administrators use
 * {@link PRODUCT_MODULE_ROLES} instead.
 */
const PERSONA_HOME_PRODUCTS: Record<Exclude<WorkspacePersona, "admin">, ReadonlySet<Product>> =
  {
    student: new Set(["exxat-prism", "exxat-compliance"]),
    faculty: new Set([
      "exxat-prism",
      "exxat-one-schools",
      "exxat-curriculum-mapping",
      "exxat-compliance",
    ]),
  }

/** Directory hubs listed under Workspace management on the products home. */
const WORKSPACE_MANAGEMENT_PRODUCTS: readonly Product[] = [
  "exxat-people",
  "exxat-courses",
  "exxat-programs",
]

function isWorkspaceManagementProduct(product: Product): boolean {
  return WORKSPACE_MANAGEMENT_PRODUCTS.includes(product)
}

/** Which module roles grant admin access within each product. */
const PRODUCT_MODULE_ROLES: Partial<Record<Product, readonly ModuleAdminRole[]>> = {
  "exxat-prism": ["clin-ed", "site-placement", "site-contract-requests", "site-contract"],
  "exxat-one-schools": ["exxat-one"],
  "exxat-curriculum-mapping": ["curriculum"],
  "exxat-compliance": ["student-compliance", "faculty-compliance"],
  "exxat-exam-management": ["exam"],
}

/** Product cards may not offer every persona the session holds in a program. */
export function openableRolesForProduct(
  product: Product,
  roles: readonly WorkspaceRole[],
): WorkspaceRole[] {
  if (
    product === "exxat-curriculum-mapping" &&
    roles.includes("student") &&
    roles.includes("member")
  ) {
    return roles.filter(role => role !== "student")
  }
  return [...roles]
}

export function workspacePersona(): WorkspacePersona {
  return PERSONA_FROM_ROLE[getLoginSession().role]
}

export function isModuleAdminRole(value: unknown): value is ModuleAdminRole {
  return MODULE_ADMIN_ROLES.some(entry => entry.value === value)
}

/** Tenet admin (`administeredProgramIds: null`) with administrator role. */
export function isTenantAdminTier(): boolean {
  const session = getLoginSession()
  return session.role === "administrator" && session.administeredProgramIds === null
}

/** Program admin (PLA) with administrator role and a program list. */
export function isProgramAdminTier(): boolean {
  const session = getLoginSession()
  return (
    session.role === "administrator" &&
    Array.isArray(session.administeredProgramIds) &&
    session.administeredProgramIds.length > 0
  )
}

/**
 * Module admin roles held by this session.
 *
 * Tenet admin: every role. Program admin: explicit list, or inferred from
 * entitled products when the session predates the field.
 */
export function moduleAdminRoles(): ModuleAdminRole[] {
  const session = getLoginSession()
  if (session.role !== "administrator") return []
  if (isTenantAdminTier()) {
    return MODULE_ADMIN_ROLES.map(entry => entry.value)
  }
  const grants = adminProgramIds()
  if (grants.length > 0) {
    const roles = new Set<ModuleAdminRole>()
    for (const grant of grants) {
      for (const role of grant.moduleAdminRoles ?? []) {
        roles.add(role)
      }
    }
    if (roles.size > 0) return [...roles]
  }
  const stored = session.moduleAdminRoles
  if (Array.isArray(stored) && stored.length > 0) {
    return stored.filter(isModuleAdminRole)
  }
  return inferModuleRolesFromProducts(session.products)
}

function inferModuleRolesFromProducts(products: Product[] | null): ModuleAdminRole[] {
  const entitled =
    products ??
    (Object.keys(PRODUCT_MODULE_ROLES) as Product[]).filter(product =>
      isProductEntitled(product),
    )
  const roles = new Set<ModuleAdminRole>()
  for (const product of entitled) {
    for (const role of PRODUCT_MODULE_ROLES[product] ?? []) {
      roles.add(role)
    }
  }
  return [...roles]
}

function hasExplicitProductGrant(): boolean {
  const products = getLoginSession().products
  return Array.isArray(products) && products.length > 0
}

/** Faculty signed in through a flow that grants specific apps only (e.g. One Site). */
export function isModuleOnlyFacultySession(): boolean {
  const session = getLoginSession()
  return session.role === "member" && hasExplicitProductGrant()
}

function heldModuleRoles(programId: string | null): ModuleAdminRole[] {
  if (isTenantAdminTier()) {
    return MODULE_ADMIN_ROLES.map(entry => entry.value)
  }
  const grants = normalizeProgramPersonas(getLoginSession().programPersonas)
  if (grants && grants.length > 0) {
    if (programId) {
      return moduleAdminRolesInProgram(programId)
    }
    const roles = new Set<ModuleAdminRole>()
    for (const grant of adminProgramIds()) {
      for (const role of grant.moduleAdminRoles ?? []) {
        roles.add(role)
      }
    }
    return [...roles]
  }
  return moduleAdminRoles()
}

function productVisibleForModuleAdmin(product: Product, programId: string | null): boolean {
  const roles = PRODUCT_MODULE_ROLES[product]
  if (!roles || roles.length === 0) return false
  const held = new Set(heldModuleRoles(programId))
  if (held.size === 0) return false
  return roles.some(role => held.has(role))
}

/**
 * Whether an owned app should appear on the home index for the active role.
 *
 * Entitlement still decides what the workspace owns; this gate decides which
 * owned tiles the active persona or module admin role may open. Sign-in flows
 * that set an explicit `products` grant skip persona filtering because the flow
 * already narrowed the license.
 *
 * Faculty + Admin is two identities: Faculty opens faculty apps, Administrator
 * opens only the modules they administer. Curriculum Mapping and Exxat One stay
 * on Faculty unless the admin grant includes those module roles.
 */
/** Whether the active persona may open this owned app, not merely see it listed. */
function tenantAdminAllProductsRequestAccess(): boolean {
  return (
    getLoginSession().homeAllProductsRequestAccess === true && isTenantAdminTier()
  )
}

export function canOpenProductInActiveRole(
  product: Product,
  programId: string | null = null,
): boolean {
  if (tenantAdminAllProductsRequestAccess()) {
    if (product === "exxat-admin") {
      return canAccessInstituteManagement()
    }
    return false
  }
  return productVisibleInActiveRole(product, programId)
}

/**
 * Faculty and module-scoped admins list every school-owned app on home, split
 * into Open vs Request access. Tenet admins and broad program admins keep the
 * older model where inaccessible apps stay hidden.
 */
export function usesAppAccessTiers(programId: string | null = null): boolean {
  const persona = workspacePersona()
  if (persona === "student") return false
  if (hasExplicitProductGrant()) {
    return persona === "faculty"
  }
  if (persona === "faculty") return true
  if (persona === "admin") {
    if (isTenantAdminTier()) return tenantAdminAllProductsRequestAccess()
    return isProgramAdminTier()
  }
  return false
}

/**
 * Split owned apps into Open and Request access when {@link usesAppAccessTiers}
 * applies. Otherwise every card stays openable.
 */
export function partitionOwnedByAccess<T extends { product: Product }>(
  cards: T[],
  programId: string | null = null,
): { openable: T[]; requestAccess: T[] } {
  if (!usesAppAccessTiers(programId)) {
    return { openable: cards, requestAccess: [] }
  }
  const openable: T[] = []
  const requestAccess: T[] = []
  for (const card of cards) {
    if (canOpenProductInActiveRole(card.product, programId)) {
      openable.push(card)
    } else {
      requestAccess.push(card)
    }
  }
  return { openable, requestAccess }
}

export function productVisibleInActiveRole(
  product: Product,
  programId: string | null = null,
): boolean {
  if (product === "exxat-admin") {
    return canAccessAdminConsoleInProgram(programId)
  }

  const persona = workspacePersona()

  if (hasExplicitProductGrant()) {
    const granted = getLoginSession().products ?? []
    if (persona === "faculty") {
      if (isWorkspaceManagementProduct(product)) {
        return canReadDirectoryInProgram(programId)
      }
      return granted.includes(product)
    }
    return true
  }

  if (persona === "admin") {
    if (isTenantAdminTier()) return true
    // Program and module admins: every Workspace hub plus the faculty app set.
    if (isProgramAdminTier()) {
      if (isWorkspaceManagementProduct(product)) {
        return canReadDirectoryInProgram(programId)
      }
      if (PERSONA_HOME_PRODUCTS.faculty.has(product)) {
        return isProductEntitled(product)
      }
      return productVisibleForModuleAdmin(product, programId)
    }
    return productVisibleForModuleAdmin(product, programId)
  }

  if (persona === "faculty") {
    if (isWorkspaceManagementProduct(product)) {
      return canReadDirectoryInProgram(programId)
    }
    return PERSONA_HOME_PRODUCTS.faculty.has(product)
  }

  return PERSONA_HOME_PRODUCTS[persona].has(product)
}

export function filterCardsForActiveRole<T extends { product: Product }>(
  cards: T[],
  programId: string | null = null,
): T[] {
  if (usesAppAccessTiers(programId)) {
    return cards.filter(card =>
      card.product === "exxat-admin"
        ? canOpenProductInActiveRole(card.product, programId)
        : true,
    )
  }
  return cards.filter(card => canOpenProductInActiveRole(card.product, programId))
}

export function hasModuleAdminRole(role: ModuleAdminRole): boolean {
  return moduleAdminRoles().includes(role)
}

/** Institute management and the tenet-wide console. Tenet admin only. */
export function canAccessInstituteManagement(): boolean {
  return isTenantAdminTier()
}

/**
 * Scoped program admin console. Program admins with at least one module role,
 * or tenet admins (who also reach the institute console).
 */
export function canAccessProgramAdminConsole(): boolean {
  if (isTenantAdminTier()) return true
  return isProgramAdminTier() && moduleAdminRoles().length > 0
}

/** Whether this session may open `/admin` at all. */
export function canAccessAdminConsole(): boolean {
  return canAccessInstituteManagement() || canAccessProgramAdminConsole()
}

/**
 * Program-scoped admin console access.
 *
 * Split-persona sessions may administer Nursing but only study in Medicine. The
 * header Administrator door and `/admin` follow the active program, not every
 * grant on the account.
 */
export function canAccessAdminConsoleInProgram(programId: string | null): boolean {
  if (!canAccessAdminConsole()) return false
  if (isTenantAdminTier()) {
    return programId === null
  }
  const grants = normalizeProgramPersonas(getLoginSession().programPersonas)
  if (!grants || grants.length === 0) return true
  if (!programId) return canAccessProgramAdminConsole()
  return hasPersonaInProgram(programId, "admin")
}

/**
 * Program-scoped Directory access (People, Courses, Programs on Campus home).
 *
 * Faculty always reach the roster. Program admins reach it only on programs they
 * administer; students never do, including split-persona student programs.
 */
export function canReadDirectoryInProgram(programId: string | null): boolean {
  const session = getLoginSession()
  if (session.role === "student") return false
  /**
   * Sign-in flows that grant only specific sellable apps keep faculty persona
   * without the roster hubs.
   */
  if (session.role === "member" && hasExplicitProductGrant()) return false
  if (session.role === "member") return true
  if (isTenantAdminTier()) return true
  const grants = normalizeProgramPersonas(session.programPersonas)
  if (!grants || grants.length === 0) return true
  if (!programId) return true
  return (
    hasPersonaInProgram(programId, "admin") || hasPersonaInProgram(programId, "faculty")
  )
}

/** Filter owned app cards to those this session may administer. */
export function adminAppsForSession<T extends { product: Product }>(apps: T[]): T[] {
  if (isTenantAdminTier()) return apps
  const held = new Set(moduleAdminRoles())
  return apps.filter(app => {
    const roles = PRODUCT_MODULE_ROLES[app.product]
    return roles?.some(role => held.has(role))
  })
}

export function moduleAdminRoleLabel(role: ModuleAdminRole): string {
  return MODULE_ADMIN_ROLES.find(entry => entry.value === role)?.label ?? role
}
